<template>
  <div ref="root" :class="rootClass">
    <template v-for="side in $options.sides">
      <div
        v-if="$props[side]"
        :key="side"
        :class="$style[side]"
        @pointerdown="handlePointerDown($event, side)"
        @mousedown.stop.prevent
      />
    </template>

    <slot v-bind="{ isDragging }" />
  </div>
</template>

<script>

export default {
  name: 'Resizable',

  props: {
    top: Boolean,
    right: Boolean,
    bottom: Boolean,
    left: Boolean,
    draggingClass: String
  },

  sides: [
    'top',
    'right',
    'bottom',
    'left'
  ],

  data () {
    return {
      dragging: null,
      dragOrigin: null,
      dragOriginRect: null
    }
  },

  methods: {
    handlePointerDown (e, side) {
      e.stopPropagation()

      this.dragging = side
      this.dragOrigin = { x: e.clientX, y: e.clientY }
      this.dragOriginRect = this.$refs.root.getBoundingClientRect()
      
      window.addEventListener('mouseup', this.handlePointerUp)
      window.addEventListener('mousemove', this.handlePointerMove)
    },

    handlePointerUp (e) {
      this.$emit('change', this.getEmission(e))

      this.dragging = null
      this.dragOrigin = null
      this.dragOriginRect = null

      window.removeEventListener('mouseup', this.handlePointerUp)
      window.removeEventListener('mousemove', this.handlePointerMove)
    },

    handlePointerMove (e) {
      e.stopPropagation()

      this.$emit('input', this.getEmission(e))
    },

    getEmission (e) {
      /*
       * Because of the fact that the resizeable rect most likely grows
       * while it is being resized, it can be unreliable to use the offset
       * relative to the edge opposed of the drag-origin (e.g. drag origin
       * 'left' means the opposing edge is 'right').
       *
       * Therefore, apart from the mentioned offset a second offset is provided
       * which is calculated based on what the rect was at the start of the drag
       * (offsetToOpposedOrigin).
       */
      return {
        originSide: this.dragging,
        originRect: this.dragOriginRect,
        deltaToOrigin: this.getDeltaToDragOrigin(e),
        deltaToOpposedOrigin: this.getDeltaToOpposedSide(e, this.dragOriginRect),
        deltaToOpposed: this.getDeltaToOpposedSide(e, this.$refs.root.getBoundingClientRect()),
      }
    },

    getDeltaToOpposedSide (e, rect) {
      if (this.dragging === 'top') {
        return rect.bottom - e.clientY
      }
      
      if (this.dragging === 'right') {
        return  e.clientX - rect.left
      }
      
      if (this.dragging === 'bottom') {
        return e.clientY - rect.top
      }

      if (this.dragging === 'left') {
        return rect.right - e.clientX
      }
    },

    getDeltaToDragOrigin (e) {
      if (this.dragging === 'right' || this.dragging === 'left') {
        return e.clientX - this.dragOrigin.x
      } else {
        return e.clientY - this.dragOrigin.y
      }
    }
  },

  computed: {
    isDragging () {
      return Boolean(this.dragging)
    },

    rootClass () {
      return [
        this.$style.root,
        { [ this.draggingClass ]: this.isDragging && this.draggingClass  }
      ]
    }
  }
}

</script>

<style module>

.root {
  position: relative;
}

.top,
.right,
.bottom,
.left {
  position: absolute;
  background: transparent;
  box-shadow: none;
  transition: box-shadow 200ms;
}

.top:hover,
.right:hover,
.bottom:hover,
.left:hover {
  /* background: rgba(0, 0, 0, 0.2); */
}

.root:hover .top,
.root:hover .right,
.root:hover .bottom,
.root:hover .left {
  /* box-shadow: 0 0 40px 4px theme('colors.primary.default'); */
}

.top,
.bottom {
  cursor: row-resize;
  width: 100%;
  height: 4px;
}

.right,
.left {
  cursor: col-resize;
  height:  100%;
  width: 6px;
}

.top {
  top: 0;
}

.right {
  right: 0;
}

.bottom {
  bottom: 0;
}

.left {
  left: 0;
}



</style>
